import { APIStatus, LanguageConfig } from '../config/CustomEnums';
import {
  createStoreCategory,
  getStoreCategory,
  updateStoreCategory,
  deleteStoreCategories,
  getStoreCategoryList,
} from '../services/StoreCategoryAPIHelper';
import {
  createAction,
  convertPKToId,
  getObjectFromSessionStorage,
  removeFromSessionStorage,
} from '../utils';
import { apiWithResponseHandle } from './LoadingUtil';

const getInitialState = () => ({
  category: {
    name: null,
    order: null,
    subCategories: [],
    translations: [],
  },
  errorFields: [],
  formChanged: false,
  saved: -1,
  createStatus: APIStatus.none,
});

export const STORE_CATEGORY_SESSION_KEY = 'STORE_CATEGORY_SESSION_KEY';

export const CREATE_STORE_CATEGORY_SUCCESS = 'CREATE_STORE_CATEGORY_SUCCESS';
export const CREATE_STORE_CATEGORY_FAILD = 'CREATE_STORE_CATEGORY_FAILD';

export const STORE_CATEGORY_ORDER_LAST = 'STORE_CATEGORY_ORDER_LAST';

const validateForm = (data) => {
  const { name, order, subCategories = [], translations = [] } = data;
  const errorFields = [];
  if (name === '' || name === null || name === undefined) {
    errorFields.push({
      field: 'name',
      message: 'Please provide a name',
    });
  }
  const displayPriority = parseInt(order);
  if (isNaN(displayPriority)) {
    errorFields.push({
      field: 'order',
      message: 'Minimum value is 1',
    });
  }
  subCategories.forEach((subCategory, index) => {
    const { name, order } = subCategory;
    const hasName = !(name === '' || name === null || name === undefined);
    const displayPriority = parseInt(order);
    if (!isNaN(displayPriority) || hasName) {
      if (!hasName) {
        errorFields.push({
          field: `sub-category-name-en-${index + 1}`,
          message: 'Please provide a subcategory name',
        });
      }
      if (isNaN(displayPriority)) {
        errorFields.push({
          field: `sub-category-order-${index + 1}`,
          message: 'Minimum value is 1',
        });
      }
    }
  });
  return errorFields;
};
const assembleStoreSubCategory = (subCategory) => {
  const translations = [];
  const apiTranslations = subCategory.translations.edges || [];
  apiTranslations.forEach((item) => {
    let language = item.node.language;
    if (language === 'ZH_HANS') {
      language = LanguageConfig.simplifiedChinese;
    }
    if (language === 'ZH_HANT') {
      language = LanguageConfig.traditionalChinese;
    }
    translations.push({
      name: item.node.name,
      id: item.node.pk,
      language: language,
    });
  });
  return {
    id: subCategory.pk,
    name: subCategory.name,
    order: subCategory.displayPriority,
    translations,
  };
};

const fixTranslations = (translations) => {
  const translationsToDelete = [];
  const fixedTranslations = translations.filter((translation) => {
    const { name, id } = translation;
    if (id && (name === '' || name === null || name === undefined)) {
      translationsToDelete.push(id);
      return false;
    }
    return true;
  });
  return [translationsToDelete, fixedTranslations];
};

const assembleStoreCategory = (category) => {
  const translations = [];
  const apiTranslations = category.translations.edges || [];
  apiTranslations.forEach((item) => {
    let language = item.node.language;
    if (language === 'ZH_HANS') {
      language = LanguageConfig.simplifiedChinese;
    }
    if (language === 'ZH_HANT') {
      language = LanguageConfig.traditionalChinese;
    }
    translations.push({
      name: item.node.name,
      id: item.node.pk,
      language: language,
    });
  });
  const subCategories = category.subcategories.edges.map(({ node }) =>
    assembleStoreSubCategory(node),
  );
  return {
    name: category.name,
    order: category.displayPriority,
    subCategories,
    translations,
  };
};

export default {
  namespace: 'createStoreCategory',
  state: getInitialState(),
  reducers: {
    updateState(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    reset(state, { payload }) {
      return getInitialState();
    },
    assembleStoreCategory(state, { payload }) {
      const { category } = payload;
      return {
        ...state,
        category: assembleStoreCategory(category),
      };
    },
    loadStoreCategoryFromSessionStorage(state, { payload }) {
      try {
        const category = getObjectFromSessionStorage(
          STORE_CATEGORY_SESSION_KEY,
        );
        if (category) {
          return {
            ...state,
            category: {
              ...category,
            },
          };
        }
      } catch (error) {
        console.log(error);
      }
      return {
        ...state,
      };
    },
    formHasChanged(state, { payload }) {
      return {
        ...state,
        formChanged: true,
      };
    },
  },
  effects: {
    getCategory: [
      function* ({ payload }, { call, select, put, all }) {
        yield put({
          type: 'updateState',
          payload: { createStatus: APIStatus.calling },
        });
        const id = convertPKToId('StoreCategoryNode', payload.id);
        const serviceArgs = [getStoreCategory, id];
        function* onSuccess(data) {
          console.log('getOneStore onSuccess :', data);
          yield all([
            put({
              type: 'assembleStoreCategory',
              payload: { category: data.storeCategory },
            }),
            put({
              type: 'updateState',
              payload: { createStatus: APIStatus.none },
            }),
          ]);
        }
        function* onError(err) {
          console.log('getOneStoreCategory onError :', err);
          yield put({
            type: 'updateState',
            payload: { createStatus: APIStatus.failed },
          });
        }
        function* onArgumentsError(err) {
          console.log('getOneStoreCategory arguments error :', err);
          yield put({
            type: 'updateState',
            payload: { createStatus: APIStatus.failed },
          });
        }
        yield apiWithResponseHandle(
          serviceArgs,
          onSuccess,
          onError,
          onArgumentsError,
        );
      },
      { type: 'takeLatest' },
    ],
    checkAndSave: [
      function* ({ payload }, { call, select, put, take, race }) {
        const { name, order, subCategories, translations } = payload;
        const errorFields = validateForm(payload);
        if (errorFields && errorFields.length > 0) {
          yield put({ type: 'updateState', payload: { errorFields } });
          return;
        }
        yield put({
          type: 'updateState',
          payload: { createStatus: APIStatus.calling },
        });
        yield put(
          createAction('createStoreCategory')({
            name: name,
            order,
            subCategories,
            translations,
          }),
        );
        const [success, failed] = yield race([
          take(CREATE_STORE_CATEGORY_SUCCESS),
          take(CREATE_STORE_CATEGORY_FAILD),
        ]);
        if (success) {
          yield put({
            type: 'updateState',
            payload: { formChanged: false, createStatus: APIStatus.success },
          });
          removeFromSessionStorage(STORE_CATEGORY_SESSION_KEY);
          console.log(success);
        } else {
          yield put({
            type: 'updateState',
            payload: { createStatus: APIStatus.failed },
          });
          console.log(failed);
        }
      },
      { type: 'takeLatest' },
    ],
    checkAndUpdate: [
      function* ({ payload }, { call, select, put, all }) {
        const {
          name,
          order,
          subCategories,
          categoryID,
          translations,
        } = payload;
        const id = parseInt(categoryID);
        if (isNaN(id)) {
          return;
        }
        const errorFields = validateForm(payload);
        if (errorFields && errorFields.length > 0) {
          yield put({ type: 'updateState', payload: { errorFields } });
          return;
        }
        yield put({
          type: 'updateState',
          payload: { createStatus: APIStatus.calling },
        });
        const [translationsToDelete, fixedTranslations] = fixTranslations(
          translations,
        );

        const input = {
          name,
          displayPriority: parseInt(order),
          subcategories: subCategories.map((subCategory) => {
            const [translationsToDelete, fixedTranslations] = fixTranslations(
              subCategory.translations,
            );
            const data = {
              name: subCategory.name,
              displayPriority: parseInt(subCategory.order),
              translationsToDelete,
              translations: fixedTranslations,
            };
            if (subCategory.id) {
              data.id = subCategory.id;
            }
            return data;
          }),
          translations: fixedTranslations,
          translationsToDelete,
          id,
        };
        console.log(input);
        const serviceArgs = [updateStoreCategory, input];

        function* onSuccess(data) {
          yield put({
            type: 'updateState',
            payload: { formChanged: false, createStatus: APIStatus.success },
          });
          removeFromSessionStorage(STORE_CATEGORY_SESSION_KEY);
        }
        function* onFailed(response) {
          yield put({
            type: 'updateState',
            payload: { createStatus: APIStatus.failed },
          });
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
      },
      { type: 'takeLatest' },
    ],
    createStoreCategory: [
      function* ({ payload }, { call, put, all }) {
        const { name, order, subCategories, translations } = payload;
        const input = {
          name,
          displayPriority: parseInt(order),
          translations,
          subcategories: subCategories
            .filter(
              (subCategory) => !isNaN(subCategory.order) && subCategory.name,
            )
            .map((subCategory) => ({
              name: subCategory.name,
              displayPriority: parseInt(subCategory.order),
              translations: subCategory.translations,
            })),
        };
        const serviceArgs = [createStoreCategory, input];

        function* onSuccess(data) {
          yield put({ type: CREATE_STORE_CATEGORY_SUCCESS });
        }
        function* onFailed(response) {
          yield put({ type: CREATE_STORE_CATEGORY_FAILD });
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
      },
      { type: 'takeLatest' },
    ],
    updateCategoryOrder: [
      function* ({ payload }, { call, put, select }) {
        const { id, order, afterAction } = payload;
        const input = {
          id,
        };

        let getStoreCategoryListError = false;
        if (order === STORE_CATEGORY_ORDER_LAST) {
          const serviceArgs = [getStoreCategoryList, '', ''];

          function* onSuccess(data) {
            input.displayPriority = data?.storeCategories?.totalCount;
          }
          function* onFailed(response) {
            getStoreCategoryListError = true;
          }
  
          yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
          if (getStoreCategoryListError) {
            return;
          }
        } else {
          input.displayPriority = order;
        }

        const serviceArgs = [updateStoreCategory, input];

        yield apiWithResponseHandle(serviceArgs);

        if (afterAction) {
          afterAction();
        }
      },
      {
        type: 'takeEvery',
      },
    ],
    deleteCategories: [
      function* ({ payload }, { call, put, select }) {
        const { ids, afterAction } = payload;

        const serviceArgs = [deleteStoreCategories, ids];

        yield apiWithResponseHandle(serviceArgs);

        if (afterAction) {
          afterAction();
        } else {
          yield put({
            type: 'updateState',
            payload: { createStatus: APIStatus.success },
          });
        }
      },
      {
        type: 'takeEvery',
      },
    ],
    duplicateCategory: [
      function* ({ payload }, { call, put, race, take }) {
        const { pk, afterAction } = payload;
        const id = convertPKToId('StoreCategoryNode', pk);

        const serviceArgs = [getStoreCategory, id];

        function* onSuccess(data) {
          const category = assembleStoreCategory(data?.storeCategory);
          yield put(
            createAction('createStoreCategory')({
              name: `copy of ${category.name}`,
              order: category.order + 1,
              subCategories: category.subCategories.map((subCategory) => ({
                order: subCategory.order,
                name: `copy of ${subCategory.name}`,
                translations: subCategory.translations.map((translation) => ({
                  language: translation.language,
                  name: translation.name,
                })),
              })),
              translations: category.translations.map((translation) => ({
                language: translation.language,
                name: translation.name,
              })),
            }),
          );
          if (afterAction) {
            afterAction();
          }
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      {
        type: 'takeEvery',
      },
    ],
  },
};
